import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { BaseDialog } from 'src/library/components/BaseDialog.js';
import { LocalDate } from 'src/utilities/local-date.js';
import { t } from 'src/i18n';

import 'src/library/elements/d-section';
import 'src/library/elements/d-action';
import 'src/library/fields/d-view-info';
import 'src/library/editors/elements/d-edit-number';
import 'src/library/editors/elements/d-edit-text';
import 'src/library/editors/elements/d-checkbox';
import 'src/library/editors/elements/d-select-checkbox';

import { overdueIssues } from './issue-escalation-overdue';
import type { DialogActionInput } from '../../library/components/DialogActionInput';
import type { SelectCheckboxOption } from '../../library/editors/elements/select-checkbox-option';
import type {
  IssueDeadlineExtension,
  IssueEscalationCandidate,
  IssueEscalationDialogInput,
  IssueEscalationDialogResult,
} from './issue-escalation';

/**
 * Dialog for å sette frist og varslingsmottakere for avvik (krav 1.2.1).
 *
 * Åpnes fra «Mer»-menyen på avvikssiden. Lar brukeren ta i bruk frist, velge
 * standard antall dager til frist og hvilke personer (ansatte/kontaktpersoner)
 * som skal varsles, og forhåndsviser – med kun ikke-sensitive opplysninger –
 * hvilke avvik som er over frist. Enkeltavvik kan få frist forlenget med en
 * begrunnelse; forlengelsene lagres via `:extend-deadline` når dialogen lagres.
 */
@customElement('d-issue-escalation-dialog')
export class DIssueEscalationDialog extends BaseDialog<IssueEscalationDialogInput, IssueEscalationDialogResult> {
  static readonly styles = BaseDialog.styles.concat(css`
    d-edit-number {
      max-width: 220px;
    }
    .preview {
      margin-top: 12px;
      border-top: 1px solid var(--borderColor);
      padding-top: 12px;
    }
    .preview-heading {
      font-weight: bold;
      margin: 12px 0 8px 0;
    }
    ul.candidates {
      margin: 0;
      padding: 0;
      list-style: none;
    }
    ul.candidates li {
      padding: 6px 0;
      border-bottom: 1px solid var(--borderColorLight);
      font-size: 14px;
    }
    .candidate-row {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: 4px 12px;
    }
    .candidate-row .date {
      font-weight: bold;
      min-width: 110px;
    }
    .candidate-row .overdue {
      color: var(--alertColor, #c00);
    }
    .candidate-row .spacer {
      flex: 1;
    }
    .snooze-form {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-end;
      gap: 8px;
      margin: 8px 0 4px 0;
    }
    .snooze-form d-edit-number {
      max-width: 130px;
    }
    .snooze-form d-edit-text {
      flex: 1;
      min-width: 160px;
    }
    .none {
      color: var(--themeColorDarkerThree, #555);
    }
  `);
  @property({ type: Boolean })
  show = true;
  @property({ type: Number })
  width = 600;
  @property({ type: Number })
  organizationId = 0;
  @property({ type: Array })
  personOptions: SelectCheckboxOption[] = [];
  @property({ type: Array })
  candidates: IssueEscalationCandidate[] = [];
  @state()
  private enabled = false;
  @state()
  private days = 14;
  @state()
  private recipients: string[] = [];
  @state()
  private notifyAssignee = true;
  @state()
  private pendingExtensions: Record<string, IssueDeadlineExtension> = {};
  @state()
  private extendingUuid = '';
  @state()
  private extendDays = 7;
  @state()
  private extendReason = '';

  protected get calculatedTitle(): string {
    return t('issues.escalation.dialogTitle');
  }

  private get hasRecipient(): boolean {
    return this.recipients.length > 0 || this.notifyAssignee;
  }

  protected get calculatedFooterActions(): DialogActionInput[] {
    return [
      { name: t('common:cancel'), action: 'cancel' },
      {
        name: t('common:save'),
        action: 'save',
        main: true,
        disabled: this.days < 1 || (this.enabled && !this.hasRecipient),
      },
    ];
  }

  private get overdue() {
    return overdueIssues(this.candidates, LocalDate.now(), this.pendingExtensions);
  }

  private get extendedCandidates() {
    return this.candidates.filter((c) => this.pendingExtensions[c.uuid]);
  }

  private startExtend(uuid: string) {
    this.extendingUuid = uuid;
    this.extendDays = 7;
    this.extendReason = '';
  }

  private confirmExtend(uuid: string) {
    const deadline = LocalDate.now().plusDays(this.extendDays).toString();
    this.pendingExtensions = {
      ...this.pendingExtensions,
      [uuid]: { uuid, deadline, reason: this.extendReason.trim() },
    };
    this.extendingUuid = '';
  }

  private removeExtend(uuid: string) {
    const { [uuid]: _removed, ...rest } = this.pendingExtensions;
    this.pendingExtensions = rest;
  }

  private renderExtendForm(uuid: string) {
    return html`
      <div class="snooze-form">
        <d-edit-number
          label=${t('issues.escalation.snoozeDaysLabel')}
          .min=${1}
          .value=${this.extendDays}
          @value-changed=${(e: CustomEvent) => (this.extendDays = e.detail.value)}
        ></d-edit-number>
        <d-edit-text
          label=${t('issues.escalation.snoozeReasonLabel')}
          placeholder=${t('issues.escalation.snoozeReasonPlaceholder')}
          .value=${this.extendReason}
          @value-changed=${(e: CustomEvent) => (this.extendReason = e.detail.value)}
        ></d-edit-text>
        <d-action @click=${() => this.confirmExtend(uuid)} ?disabled=${this.extendReason.trim() === ''}
          >${t('common:save')}</d-action
        >
        <d-action @click=${() => (this.extendingUuid = '')}>${t('common:cancel')}</d-action>
      </div>
    `;
  }

  private renderOverdue() {
    const overdue = this.overdue;
    return html`
      <div class="preview-heading">
        ${overdue.length === 0
          ? t('issues.escalation.noneOverdue')
          : t('issues.escalation.overdueHeading', { count: overdue.length })}
      </div>
      ${overdue.length === 0
        ? nothing
        : html`<ul class="candidates">
            ${overdue.map(
              (c) =>
                html`<li>
                  <div class="candidate-row">
                    <span class="date">${LocalDate.fromString(c.deadline).toStringForDisplay()}</span>
                    <span>${c.statusLabel}</span>
                    <span>${c.responsibleName ? c.responsibleName : t('issues.escalation.notAssigned')}</span>
                    <span class="overdue">${t('issues.escalation.daysOverdue', { count: c.daysOverdue })}</span>
                    <span class="spacer"></span>
                    ${this.extendingUuid === c.uuid
                      ? nothing
                      : html`<d-action @click=${() => this.startExtend(c.uuid)}
                          >${t('issues.escalation.snoozeAction')}</d-action
                        >`}
                  </div>
                  ${this.extendingUuid === c.uuid ? this.renderExtendForm(c.uuid) : nothing}
                </li>`,
            )}
          </ul>`}
      ${overdue.length > 0 && !this.hasRecipient
        ? html`<div class="none">${t('issues.escalation.selectRecipientWarning')}</div>`
        : nothing}
    `;
  }

  private renderExtended() {
    const extended = this.extendedCandidates;
    if (extended.length === 0) {
      return nothing;
    }
    return html`
      <div class="preview-heading">${t('issues.escalation.snoozedHeading')}</div>
      <ul class="candidates">
        ${extended.map((c) => {
          const extension = this.pendingExtensions[c.uuid];
          return html`<li>
            <div class="candidate-row">
              <span class="date">${LocalDate.fromString(c.reportedDate).toStringForDisplay()}</span>
              <span
                >${t('issues.escalation.snoozedUntil', {
                  date: LocalDate.fromString(extension.deadline).toStringForDisplay(),
                })}</span
              >
              <span class="none">${extension.reason}</span>
              <span class="spacer"></span>
              <d-action @click=${() => this.removeExtend(c.uuid)}>${t('issues.escalation.undoAction')}</d-action>
            </div>
          </li>`;
        })}
      </ul>
    `;
  }

  renderBody() {
    return html`
      <d-section>
        <d-view-info .content=${t('issues.escalation.info')}></d-view-info>
        <d-checkbox
          class="fullWidth"
          wrap-label
          .optionLabel=${t('issues.escalation.enabledLabel')}
          .checked=${this.enabled}
          @checked-changed=${(e: CustomEvent) => (this.enabled = e.detail.checked)}
        ></d-checkbox>
        ${this.enabled
          ? html` <d-edit-number
                label=${t('issues.escalation.daysLabel')}
                .min=${1}
                .value=${this.days}
                @value-changed=${(e: CustomEvent) => (this.days = e.detail.value)}
              ></d-edit-number>
              <d-checkbox
                class="fullWidth"
                wrap-label
                .optionLabel=${t('issues.escalation.notifyAssigneeLabel')}
                .checked=${this.notifyAssignee}
                @checked-changed=${(e: CustomEvent) => (this.notifyAssignee = e.detail.checked)}
              ></d-checkbox>
              <d-select-checkbox
                checklist
                class="fullWidth"
                label=${t('issues.escalation.recipientsLabel')}
                .options=${this.personOptions}
                .value=${this.recipients}
                @value-changed=${(e: CustomEvent) => (this.recipients = e.detail.value)}
              ></d-select-checkbox>
              <div class="preview">${this.renderOverdue()} ${this.renderExtended()}</div>`
          : nothing}
      </d-section>
    `;
  }

  protected fetchResult(detail: string | undefined): IssueEscalationDialogResult {
    if (detail === 'save') {
      return {
        action: 'save',
        settings: {
          enabled: this.enabled,
          days: this.days,
          recipients: this.recipients,
          notifyAssignee: this.notifyAssignee,
        },
        extensions: Object.values(this.pendingExtensions),
      };
    }
    return { action: 'cancel' };
  }

  protected initializeDialog(input: IssueEscalationDialogInput) {
    this.organizationId = input.organizationId;
    this.personOptions = input.personOptions;
    this.candidates = input.candidates;
    this.enabled = input.settings.enabled;
    this.days = input.settings.days;
    this.recipients = input.settings.recipients;
    this.notifyAssignee = input.settings.notifyAssignee;
    this.pendingExtensions = {};
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-issue-escalation-dialog': DIssueEscalationDialog;
  }
}
